import { createSelector } from "reselect";
import get from "lodash/get";
import filter from "lodash/filter";
import map from "lodash/map";
import includes from "lodash/includes";
import some from "lodash/some";
import { TELCO_DISCOUNT_TYPES, TELCO_BENEFIT_TYPES } from "utils/constants";
import { genericSelector, selectAppSettings } from "./appSettings";
import { upper, getChannelFromUrl } from "utils/helpers";

const referralCodeDomain = state => get(state.api, "referralCode.data", {});
const referralCodeErrorReasonDomain = state =>
  get(state.api, "referralCode.errorReason", "");
const isLoading = state => get(state.api, "referralCode.loading", false);
const planServiceDomain = state => get(state.api, "planService.data", {});
// ***************************************************************
// ****************** referral code selectors ********************
// ***************************************************************

export const referralCodeSelector = createSelector(
  referralCodeDomain,
  referralCodeErrorReasonDomain,
  isLoading,
  planServiceDomain,
  (referralCode, errorReason, loading, planService) => {
    const onetime_sim_activation_fee = get(planService, "activation_charge", 0);
    let benefits = filter(
      referralCode.benefits,
      benefit =>
        !(
          benefit?.type === TELCO_BENEFIT_TYPES.TELCO_ADDON &&
          benefit?.product?.price?.value > 0
        )
    );
    // Filter registration discount from benefits if onetime_sim_activation_fee is 0
    if (!onetime_sim_activation_fee) {
      benefits = filter(
        benefits,
        benefit =>
          benefit?.type !== TELCO_DISCOUNT_TYPES.TELCO_REGISTRATION_DISCOUNT
      );
    }
    benefits = filter(
      benefits,
      benefit => !benefit?.fulfillment_meta?.start_from_month
    );
    return {
      loading,
      benefits,
      title: referralCode.title || "",
      reason: referralCode.reason || referralCode.message_code || errorReason,
      valid: referralCode.code === 0,
      value: referralCode.referral_code || "",
      port_in_only: referralCode.port_in_only || false
    };
  }
);

export const referralCodeAddonsSelector = createSelector(
  referralCodeDomain,
  genericSelector,
  (referralCode, generic) => {
    const benefits = filter(referralCode.benefits, benefit => {
      const isAddon = benefit?.type === TELCO_BENEFIT_TYPES.TELCO_ADDON;
      const isPaid = benefit?.product?.price?.value > 0;
      const isAllowedFree = includes(
        generic?.allowed_free_referral_addon_skus,
        benefit?.product?.id
      );
      return isAddon && (isPaid || isAllowedFree);
    });
    return map(benefits, benefit => benefit.product);
  }
);

export const isWalletPromoCodeSelector = createSelector(
  referralCodeSelector,
  genericSelector,
  selectAppSettings,
  (referralCode, generic, appSettings) => {
    const walletPromoCodes = generic?.wallet_promo_codes || ["WALLET"];
    const enableWalletPaymentBeta =
      getChannelFromUrl() === "e-sim" &&
      get(appSettings, "enable_wallet_payment_beta");
    const enableWalletPayment =
      getChannelFromUrl() === "self_activation" &&
      get(appSettings, "enable_wallet_payment");
    const enableWallet = enableWalletPaymentBeta || enableWalletPayment;

    const isWalletPromoCode =
      enableWallet &&
      referralCode?.valid &&
      some(walletPromoCodes, term =>
        upper(referralCode?.value)?.includes(term)
      );

    return isWalletPromoCode;
  }
);

// ***************************************************************
// ****************** referral code makeSelect *******************
// ***************************************************************

export const makeSelectReferralCode = () => referralCodeSelector;
export const makeSelectReferralCodeAddons = () => referralCodeAddonsSelector;

export const makeSelectIsWalletPromoCode = () => isWalletPromoCodeSelector;
